import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { axiosInstance, fmt, getApiErrorMessage, inputCls, Modal } from './_shared';

export default function RegistrationsTab() {
  const [regs, setRegs] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterEvent, setFilterEvent] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [search, setSearch] = useState('');
  const [approving, setApproving] = useState(false);
  const [showBulkRegister, setShowBulkRegister] = useState(false);
  const [bulkRegistering, setBulkRegistering] = useState(false);
  const [bulkRegResult, setBulkRegResult] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const params = {};
        if (filterEvent) params.event_id = filterEvent;
        if (filterRole) params.role = filterRole;
        if (filterStatus) params.status = filterStatus;
        const [regRes, evRes] = await Promise.all([
          axiosInstance.get('/v1/admin/registrations', { params }),
          axiosInstance.get('/admin/events'),
        ]);
        if (!cancelled) {
          setRegs(regRes.data);
          setEvents(evRes.data);
        }
      } catch {
        if (!cancelled) toast.error('Failed to load registrations');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [filterEvent, filterRole, filterStatus]);

  const STATUS_COLORS = {
    approved: 'bg-green-100 text-green-700',
    pending: 'bg-yellow-100 text-yellow-700',
    rejected: 'bg-red-100 text-red-600',
    draft: 'bg-gray-100 text-gray-500',
    approved_pending_payment: 'bg-blue-100 text-blue-700',
  };
  const ROLE_COLORS = {
    player: 'bg-emerald-100 text-emerald-700',
    team_owner: 'bg-purple-100 text-purple-700',
    sponsor: 'bg-orange-100 text-orange-700',
  };

  const exportCSV = () => {
    if (!regs.length) return;
    const headers = ['Event', 'Name', 'Email', 'Role', 'Status', 'Payment', 'Registered At'];
    const rows = regs.map(r => [
      r.event_title || '',
      r.user_name || '',
      r.user_email || '',
      r.role || '',
      r.status || '',
      r.payment_amount > 0 ? `₹${(r.payment_amount / 100).toLocaleString()}` : 'Free',
      r.created_at ? new Date(r.created_at).toLocaleDateString() : '',
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'registrations.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const pendingPlayerCount = regs.filter(r => r.role === 'player' && r.status === 'pending').length;

  const handleBulkApprove = async () => {
    if (!filterEvent) return;
    const eventName = events.find(e => e.id === filterEvent)?.title || 'this event';
    if (!window.confirm(`Approve all ${pendingPlayerCount} pending player registration(s) for "${eventName}"?`)) return;
    setApproving(true);
    try {
      const res = await axiosInstance.post(`/v1/admin/events/${filterEvent}/registrations/bulk-approve-players`);
      toast.success(`${res.data.approved_count} player(s) approved successfully`);
      // Reload with current filters
      const params = { event_id: filterEvent };
      if (filterRole) params.role = filterRole;
      if (filterStatus) params.status = filterStatus;
      const regRes = await axiosInstance.get('/v1/admin/registrations', { params });
      setRegs(regRes.data);
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Bulk approval failed'));
    } finally {
      setApproving(false);
    }
  };

  const handleBulkRegister = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !filterEvent) return;
    setBulkRegistering(true);
    setBulkRegResult(null);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axiosInstance.post(`/v1/admin/events/${filterEvent}/registrations/bulk-register-players`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setBulkRegResult(res.data);
      toast.success(`${res.data.summary.created} players registered`);
      // Refresh registrations
      const params = {};
      if (filterEvent) params.event_id = filterEvent;
      if (filterRole) params.role = filterRole;
      if (filterStatus) params.status = filterStatus;
      const regRes = await axiosInstance.get('/v1/admin/registrations', { params });
      setRegs(regRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to register players');
    } finally {
      setBulkRegistering(false);
      e.target.value = '';
    }
  };

  const downloadRegTemplate = async () => {
    if (!filterEvent) return;
    try {
      const res = await axiosInstance.get(`/v1/admin/events/${filterEvent}/registrations/bulk-register-template`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `bulk_register_players.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download template'); }
  };


  const filtered = regs.filter(r => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (
      r.user_name?.toLowerCase().includes(q) ||
      r.user_email?.toLowerCase().includes(q) ||
      r.event_title?.toLowerCase().includes(q) ||
      r.registration_data?.owner_name?.toLowerCase().includes(q) ||
      r.registration_data?.team_name?.toLowerCase().includes(q)
    );
  });

  return (
    <div className="space-y-4" data-testid="registrations-tab">
      {/* Filters row */}
      <div className="flex gap-2 flex-wrap">
        <select className={`${inputCls} flex-1 min-w-[120px]`} value={filterEvent} onChange={e => setFilterEvent(e.target.value)} data-testid="reg-filter-event">
          <option value="">All Events</option>
          {events.map(ev => <option key={ev.id} value={ev.id}>{ev.title}</option>)}
        </select>
        <select className={`${inputCls} w-32`} value={filterRole} onChange={e => setFilterRole(e.target.value)} data-testid="reg-filter-role">
          <option value="">All Roles</option>
          <option value="player">Player</option>
          <option value="team_owner">Team Owner</option>
          <option value="sponsor">Sponsor</option>
        </select>
        <select className={`${inputCls} w-36`} value={filterStatus} onChange={e => setFilterStatus(e.target.value)} data-testid="reg-filter-status">
          <option value="">All Status</option>
          <option value="approved">Approved</option>
          <option value="pending">Pending</option>
          <option value="draft">Draft</option>
          <option value="rejected">Rejected</option>
          <option value="approved_pending_payment">Pending Payment</option>
        </select>
        <button onClick={exportCSV} disabled={!regs.length}
          className="px-4 py-2.5 bg-green-500 text-white font-bold text-xs rounded-xl flex items-center gap-1.5 disabled:opacity-50"
          data-testid="reg-export-csv">
          <i className="ph-bold ph-download-simple" /> CSV
        </button>
      </div>
      {/* Bulk Approve Players — shown only when an event is selected and has pending players */}
      {filterEvent && pendingPlayerCount > 0 && (
        <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
          <div className="flex items-center gap-2">
            <i className="ph-bold ph-users text-amber-600"></i>
            <p className="text-sm font-bold text-amber-800">
              {pendingPlayerCount} pending player{pendingPlayerCount !== 1 ? 's' : ''} awaiting approval
            </p>
          </div>
          <button
            onClick={handleBulkApprove}
            disabled={approving}
            className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white font-bold text-xs rounded-lg transition-colors disabled:opacity-50"
            data-testid="bulk-approve-players-btn"
          >
            {approving ? 'Approving...' : 'Approve All Players'}
          </button>
        </div>
      )}
      {/* Bulk Register Players — shown when an event is selected */}
      {filterEvent && (
        <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
          <div className="flex items-center gap-2">
            <i className="ph-bold ph-user-plus text-blue-600"></i>
            <p className="text-sm font-bold text-blue-800">Bulk register players via CSV</p>
          </div>
          <button
            onClick={() => { setShowBulkRegister(true); setBulkRegResult(null); }}
            className="px-4 py-2 bg-[#2B3A67] hover:bg-[#1e2a4a] text-white font-bold text-xs rounded-lg transition-colors"
            data-testid="bulk-register-players-btn"
          >
            <i className="ph-bold ph-upload-simple mr-1"></i>
            Bulk Register Players
          </button>
        </div>
      )}

      {/* Bulk Register Modal */}
      {showBulkRegister && filterEvent && (
        <Modal title="Bulk Register Players" onClose={() => setShowBulkRegister(false)}>
          <div className="space-y-4">
            <div className="bg-blue-50 rounded-xl p-3">
              <p className="text-sm font-bold text-blue-800 mb-1">
                {events.find(e => e.id === filterEvent)?.title || 'Selected Event'}
              </p>
              <p className="text-xs text-blue-600">Players will be registered with status: Approved, Payment: Paid (waived)</p>
            </div>

            <p className="text-sm text-gray-600">
              Upload CSV with columns: <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono">email, player_name, playing_role, batting_style, bowling_style, bowling_type, category</code>
            </p>
            <p className="text-xs text-gray-400">
              Only <strong>email</strong> is required. Users must already exist in the system. Playing role options: Batter, Bowler, All Rounder, Wicketkeeper. Batting style: Right Hand, Left Hand.
            </p>

            <div className="flex gap-3">
              <button
                onClick={downloadRegTemplate}
                className="flex-1 py-2.5 border border-[#2B3A67] text-[#2B3A67] rounded-xl font-bold text-sm flex items-center justify-center gap-2 hover:bg-[#2B3A67]/5"
                data-testid="download-reg-template-btn"
              >
                <i className="ph-bold ph-download-simple"></i>
                Download Template
              </button>
              <label className="flex-1 py-2.5 bg-[#3AAFA9] text-white rounded-xl font-bold text-sm flex items-center justify-center gap-2 cursor-pointer hover:bg-[#2D8A85]">
                <i className="ph-bold ph-file-csv"></i>
                {bulkRegistering ? 'Registering...' : 'Upload CSV'}
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleBulkRegister}
                  disabled={bulkRegistering}
                  className="hidden"
                  data-testid="bulk-register-file-input"
                />
              </label>
            </div>

            {bulkRegResult && (
              <div className="space-y-3 pt-3 border-t border-gray-100">
                <h4 className="font-bold text-sm text-[#2B3A67]">Registration Results</h4>
                <div className="grid grid-cols-3 gap-2">
                  <div className="bg-green-50 rounded-xl p-3 text-center">
                    <p className="text-2xl font-black text-green-600">{bulkRegResult.summary.created}</p>
                    <p className="text-[10px] text-green-600 font-medium">Registered</p>
                  </div>
                  <div className="bg-amber-50 rounded-xl p-3 text-center">
                    <p className="text-2xl font-black text-amber-600">{bulkRegResult.summary.skipped}</p>
                    <p className="text-[10px] text-amber-600 font-medium">Skipped</p>
                  </div>
                  <div className="bg-red-50 rounded-xl p-3 text-center">
                    <p className="text-2xl font-black text-red-600">{bulkRegResult.summary.errors}</p>
                    <p className="text-[10px] text-red-600 font-medium">Errors</p>
                  </div>
                </div>

                {bulkRegResult.skipped?.length > 0 && (
                  <div>
                    <p className="text-xs font-bold text-amber-600 mb-1">Skipped:</p>
                    <div className="max-h-24 overflow-y-auto text-xs text-gray-500 space-y-0.5">
                      {bulkRegResult.skipped.map((s, i) => (
                        <p key={i}>Row {s.row}: {s.email} — {s.reason}</p>
                      ))}
                    </div>
                  </div>
                )}

                {bulkRegResult.errors?.length > 0 && (
                  <div>
                    <p className="text-xs font-bold text-red-600 mb-1">Errors:</p>
                    <div className="max-h-24 overflow-y-auto text-xs text-gray-500 space-y-0.5">
                      {bulkRegResult.errors.map((e, i) => (
                        <p key={i}>Row {e.row}: {e.email} — {e.reason}</p>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </Modal>
      )}
      {/* Search */}
      <div className="relative">
        <i className="ph-bold ph-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by name, email, team..."
          className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm bg-white"
          data-testid="reg-search-input"
        />
      </div>
      <p className="text-xs text-gray-400">{filtered.length} of {regs.length} registrations</p>
      {loading ? (
        <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#E54562]" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-gray-400"><i className="ph-duotone ph-list-bullets text-5xl mb-3" /><p>{search ? `No results for "${search}"` : 'No registrations'}</p></div>
      ) : (
        <div className="space-y-3">
          {filtered.map(reg => {
            const rd = reg.registration_data || {};
            return (
              <div key={reg.id} className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1 min-w-0">
                    <p className="font-extrabold text-sm truncate">{reg.event_title}</p>
                    <p className="text-xs text-gray-400">{reg.user_name} · {reg.user_email}</p>
                  </div>
                  <p className="text-xs text-gray-300 ml-2 shrink-0">{fmt(reg.created_at)}</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${ROLE_COLORS[reg.role] || 'bg-gray-100 text-gray-600'}`}>
                    {reg.role?.replace('_', ' ')}
                  </span>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${STATUS_COLORS[reg.status] || 'bg-gray-100 text-gray-500'}`}>
                    {reg.status?.replace(/_/g, ' ')}
                  </span>
                  {reg.payment_amount > 0 && (
                    <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-teal-50 text-teal-700">
                      ₹{(reg.payment_amount / 100).toLocaleString()}
                    </span>
                  )}
                </div>
                {/* Key registration fields */}
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-1">
                  {reg.role === 'player' && <>
                    {rd.playing_role && <p className="text-xs"><span className="text-gray-400">Role: </span><span className="font-bold">{rd.playing_role}</span></p>}
                    {rd.batting_style && <p className="text-xs"><span className="text-gray-400">Bat: </span><span className="font-bold">{rd.batting_style}</span></p>}
                    {rd.experience_level && <p className="text-xs"><span className="text-gray-400">Level: </span><span className="font-bold">{rd.experience_level}</span></p>}
                  </>}
                  {reg.role === 'team_owner' && <>
                    {rd.team_name && <p className="text-xs"><span className="text-gray-400">Team: </span><span className="font-bold">{rd.team_name}</span></p>}
                    {rd.ownership_type && <p className="text-xs"><span className="text-gray-400">Type: </span><span className="font-bold">{rd.ownership_type}</span></p>}
                    {rd.owner_phone && <p className="text-xs"><span className="text-gray-400">Phone: </span><span className="font-bold">{rd.owner_phone}</span></p>}
                  </>}
                  {reg.role === 'sponsor' && <>
                    {rd.sponsorship_type && <p className="text-xs"><span className="text-gray-400">Type: </span><span className="font-bold">{rd.sponsorship_type}</span></p>}
                    {rd.budget_range && <p className="text-xs"><span className="text-gray-400">Budget: </span><span className="font-bold">{rd.budget_range}</span></p>}
                  </>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

